import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as ComponentFunctions from './SettlementInvoiceUpload.Functions';
import FileDropZone from '../../Commons/FileDropZone/FileDropZone.Component';
import AzureFileUploadDrawer from '../../Commons/AzureFileUploadDrawer/AzureFileUploadDrawer.Component';
import { SETTLEMENT_ALLOWED_FILE_TYPES } from '../../../functions/AzureFileUpload.Functions';
import { AzureFileEntityTypeEnum } from '../../../models/Enums/AzureFileTypeEnum';
import { AzureFileTarget } from '../../../models/Entities/AzureFiles/AzureFileUpload';

/**
 * Settlement detail: wraps the page in a file drop target and owns the invoice upload drawer.
 * The children receive `openUpload` / `disabledReason` to render the header button.
 */
export const SettlementInvoiceUploadComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [droppedFiles, setDroppedFiles] = React.useState<File[] | undefined>(undefined);

  const disabledReason = props.owner
    ? undefined
    : props.isSettlementLoaded
      ? t('azureFileUpload.ownerUnavailable.settlement')
      : t('azureFileUpload.ownerUnavailable.loading');

  const target: AzureFileTarget | undefined = React.useMemo(
    () =>
      props.owner
        ? { entityType: AzureFileEntityTypeEnum.Settlement, entityId: props.SettlementId, owner: props.owner }
        : undefined,
    [props.SettlementId, props.owner?.ownerType, props.owner?.ownerId, props.owner?.protocolId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const openUpload = React.useCallback(() => {
    setDroppedFiles(undefined);
    setIsOpen(true);
  }, []);

  const onFilesDropped = React.useCallback((files: File[]) => {
    setDroppedFiles(files);
    setIsOpen(true);
  }, []);

  return (
    <FileDropZone
      enabled={!!target && !isOpen}
      overlayText={t('azureFileUpload.dropZone.overlaySettlement')}
      onFilesDropped={onFilesDropped}
    >
      {props.children({ openUpload, disabledReason })}
      {target && (
        <AzureFileUploadDrawer
          open={isOpen}
          onDismiss={() => setIsOpen(false)}
          title={t('azureFileUpload.titles.settlement')}
          target={target}
          allowedFileTypes={SETTLEMENT_ALLOWED_FILE_TYPES}
          initialFiles={droppedFiles}
          fileListLoader="settlement"
          allowMultiple
        />
      )}
    </FileDropZone>
  );
};

export default connect(ComponentFunctions.mapStateToProps)(
  SettlementInvoiceUploadComponent as React.ComponentType<ComponentFunctions.Props>
);
